import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import Header from '../Common/Header/Header'
import SideMenu from '../Common/SideMenu/SideMenu'
import SideDrawer from '../Common/SideDrawer/SideDrawer'
import Select from 'react-select'
import moment from 'moment'
import './Query.scss'
import {
  PAGE_CONST,
  PERMISSION_FEATURE,
  PERMISSION_TYPE,
  QUERY_TAB_TYPE,
  QUERY_STATUS,
  QUERY_STATUS_OPTIONS,
} from '../../common/constant/page-constant'
import {
  HEADER_CONFIG,
  HEADER_CONST,
  SIDEBAR_CONST,
} from '../../common/constant/common-constant'
import {
  getQueriesHelper,
  getQueryDetailsHelper,
  resolveQueryHelper,
  updateQueryStatusHelper,
  assignQueryHelper,
  updateQueryNotesHelper,
  getUserHelper,
} from '../../helper/settings-helper'
import {
  convertToTitleCase,
  formatDate,
  formatTime,
} from '../../common/utils/appUtils'
import useCheckMobileScreen from '../../common/hooks/use-check-mobile-screen-hook'
import MobileHeader from '../Common/Header/MobileHeader'
import SearchIcon from '../../assets/SearchIcon'
import Config from '../../config/config'

const STATUS_COLOR_MAP = {
  NEW: 'statusNew',
  IN_PROGRESS: 'statusInProgress',
  RESOLVED: 'statusResolved',
  CLOSED: 'statusClosed',
}

const QUERY_VIEW = {
  DASHBOARD: 'DASHBOARD',
  EPHARMA: QUERY_TAB_TYPE.EPHARMA,
  EMED: QUERY_TAB_TYPE.EMED,
}

const getDaysSince = (date) => {
  if (!date) return 0
  return moment().diff(moment(date), 'days')
}

const Query = (props) => {
  const inputRef = useRef()
  const isMobile = useCheckMobileScreen()

  const [permissions, setPermissions] = useState({})
  const [activeTab, setActiveTab] = useState(QUERY_VIEW.DASHBOARD)
  const [startDate, setStartDate] = useState(undefined)
  const [endDate, setEndDate] = useState(undefined)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState(undefined)
  const [queriesData, setQueriesData] = useState([])
  const [filteredQueriesData, setFilteredQueriesData] = useState([])
  const [queryDetails, setQueryDetails] = useState({})
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [crmUsers, setCrmUsers] = useState([])
  const [noteInput, setNoteInput] = useState('')
  const [notesHistory, setNotesHistory] = useState([])

  const [headerOptions] = useState({
    [HEADER_CONFIG.IS_BACK_VISIBLE]: false,
    [HEADER_CONFIG.TITLE]: HEADER_CONST.HOME,
  })

  useEffect(() => {
    let sDate = moment().subtract(6, 'months').format('YYYY-MM-DD')
    let eDate = moment().format('YYYY-MM-DD')
    setStartDate(sDate)
    setEndDate(eDate)
    getQueryData(sDate, eDate)
    loadCrmUsers()
  }, [])

  useEffect(() => {
    if (props.loginData?.permissions) {
      let permission = props.loginData.permissions.reduce((acc, permission) => {
        acc.features = {
          ...acc.features,
          ...permission.features,
        }
        return acc
      }, {})
      if (permission) setPermissions(permission)
    }
  }, [props.loginData])

  useEffect(() => {
    applyFilters()
  }, [activeTab, selectedStatus, searchTerm, queriesData])

  const applyFilters = () => {
    if (activeTab === QUERY_VIEW.DASHBOARD) {
      setFilteredQueriesData([])
      return
    }

    let filtered = queriesData?.filter(
      (query) => query.user_type === activeTab
    )

    if (selectedStatus) {
      filtered = filtered?.filter(
        (query) => query.status === selectedStatus
      )
    }

    if (searchTerm) {
      let term = searchTerm.toUpperCase()
      filtered = filtered?.filter(
        (query) =>
          query?.query_id?.toString()?.toUpperCase()?.includes(term) ||
          query?.name?.toUpperCase()?.includes(term) ||
          query?.mobile_no?.toUpperCase()?.includes(term) ||
          query?.subject?.toUpperCase()?.includes(term)
      )
    }

    filtered = (filtered || [])
      .map((q) => ({ ...q, daysPending: getDaysSince(q.date) }))
      .sort((a, b) => b.daysPending - a.daysPending)

    setFilteredQueriesData(filtered)
  }

  const getDashboardData = () => {
    let epharmaQueries = queriesData?.filter((q) => q.user_type === QUERY_TAB_TYPE.EPHARMA) || []
    let emedQueries = queriesData?.filter((q) => q.user_type === QUERY_TAB_TYPE.EMED) || []
    let allQueries = queriesData || []

    let statusCounts = {
      NEW: 0, IN_PROGRESS: 0, RESOLVED: 0, CLOSED: 0,
    }
    allQueries.forEach((q) => {
      if (statusCounts[q.status] !== undefined) statusCounts[q.status]++
    })

    let unresolvedQueries = allQueries
      .filter((q) => q.status !== 'RESOLVED' && q.status !== 'CLOSED')
      .map((q) => ({ ...q, daysPending: getDaysSince(q.date) }))
      .sort((a, b) => b.daysPending - a.daysPending)

    return {
      totalAll: allQueries.length,
      totalEpharma: epharmaQueries.length,
      totalEmed: emedQueries.length,
      statusCounts,
      unresolvedQueries,
    }
  }

  const getQueryData = async (sDate, eDate) => {
    setLoading(true)
    let data = await getQueriesHelper(startDate || sDate, endDate || eDate)
    setQueriesData(data || [])
    setSearchTerm('')
    setSelectedStatus(undefined)
    setLoading(false)
  }

  const loadCrmUsers = async () => {
    let data = await getUserHelper()
    setCrmUsers(data || [])
  }

  const getQueryDetails = async (query) => {
    if (!query) return
    let data = await getQueryDetailsHelper(query)
    setQueryDetails(data || {})
    setNotesHistory(Array.isArray(data?.internal_notes) ? data.internal_notes : [])
    setNoteInput('')
  }

  const handleGo = async () => {
    if (!startDate || !endDate) return
    getQueryData()
  }

  const handleRowClick = (query) => {
    setOpen(true)
    getQueryDetails(query)
  }

  const handleDrawerToggle = () => {
    if (open) {
      setQueryDetails({})
      setNoteInput('')
      setNotesHistory([])
    }
    setOpen(!open)
  }

  const handleResolve = async () => {
    if (!queryDetails) return
    await resolveQueryHelper(queryDetails)
    getQueryData()
    handleDrawerToggle()
  }

  const handleStatusUpdate = async (newStatus) => {
    if (!queryDetails) return
    await updateQueryStatusHelper(
      queryDetails.query_no,
      queryDetails.user_type,
      newStatus
    )
    getQueryData()
    handleDrawerToggle()
  }

  const handleAssign = async (crmUserId) => {
    if (!queryDetails || !crmUserId) return
    await assignQueryHelper(
      queryDetails.query_no,
      queryDetails.user_type,
      crmUserId
    )
    getQueryData()
    getQueryDetails(queryDetails)
  }

  const handleSaveNotes = async () => {
    if (!queryDetails || !noteInput.trim()) return
    let result = await updateQueryNotesHelper(
      queryDetails.query_no,
      queryDetails.user_type,
      noteInput.trim()
    )
    if (result?.notes) {
      setNotesHistory(result.notes)
    }
    setNoteInput('')
    getQueryData()
  }

  const canManage =
    permissions?.features?.[PERMISSION_FEATURE.QUERY] === PERMISSION_TYPE.MANAGE

  const getStatusClass = (status) => STATUS_COLOR_MAP[status] || 'statusNew'

  const getNextStatusAction = () => {
    let status = queryDetails?.status
    if (status === 'NEW') return { label: 'Start Progress', value: QUERY_STATUS.IN_PROGRESS }
    if (status === 'IN_PROGRESS') return { label: 'Resolve', value: QUERY_STATUS.RESOLVED }
    if (status === 'RESOLVED') return { label: 'Close', value: QUERY_STATUS.CLOSED }
    return null
  }

  const nextAction = getNextStatusAction()

  const reactSelectStyles = {
    control: (base) => ({
      border: '1px solid #D4D4D4',
      borderRadius: '0.25rem',
      display: 'flex',
      paddingTop: '20px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? 'rgb(222, 235, 255)'
        : state.isSelected
          ? '#FFF'
          : 'inherit',
      color: '#212529',
      '&:hover': {
        backgroundColor: state.isSelected
          ? '#DEEBFF'
          : 'rgb(222, 235, 255)',
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  }

  const filterSelectStyles = {
    control: (base) => ({
      border: '1px solid #D4D4D4',
      borderRadius: '0.25rem',
      display: 'flex',
      minHeight: '40px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? 'rgb(222, 235, 255)'
        : state.isSelected
          ? '#FFF'
          : 'inherit',
      color: '#212529',
      '&:hover': {
        backgroundColor: state.isSelected
          ? '#DEEBFF'
          : 'rgb(222, 235, 255)',
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  }

  return (
    <div className="containerQuery">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader headerOptions={headerOptions} />}
      <SideMenu activeItem={SIDEBAR_CONST.HELP} component={PAGE_CONST.HELP} />
      <div className="pageBody dFlex">
        <div className="queryPageWrapper">
          <div className="queryHeader dFlex aiCenter jcSpaceBetween">
            <div className="pageBodyHeading">Manage Queries</div>
            <div className="queryHeaderActions dFlex aiCenter">
              <div className="datePicker formInline dFlex">
                <div className="formGroup formFloating mb-0 flex1">
                  <input
                    type="date"
                    className="formControl"
                    placeholder="From"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                  <label>From</label>
                </div>
                <div className="formGroup formFloating ml-4 mb-0 flex1">
                  <input
                    type="date"
                    className="formControl"
                    placeholder="To"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                  <label>To</label>
                </div>
                <div className="dFlex ml-4">
                  <button
                    className="btn btnPrimary"
                    onClick={() => handleGo()}
                  >
                    SUBMIT
                  </button>
                </div>
              </div>
            </div>
          </div>

          {activeTab !== QUERY_VIEW.DASHBOARD && (
            <div className="queryFilters dFlex aiCenter mt-5">
              <div className="formGroup mb-0 searchBar expand querySearchBar">
                <input
                  ref={inputRef}
                  type="text"
                  className="formControl"
                  placeholder="Search by Query ID, Name, Phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                />
                <div className="searchIcon">
                  <SearchIcon />
                </div>
              </div>
              <Select
                className="formSelect reactSelect queryStatusFilter"
                value={selectedStatus ? { name: selectedStatus, label: convertToTitleCase(selectedStatus) } : null}
                options={QUERY_STATUS_OPTIONS}
                onChange={(option) => setSelectedStatus(option?.name)}
                placeholder="Filter by Status"
                styles={filterSelectStyles}
                getOptionLabel={(option) => option?.label}
                isClearable
              />
            </div>
          )}

          <div className="queryTabsWrapper mt-5">
            <ul className="tabs dFlex">
              <li
                className={`tabItem ${activeTab === QUERY_VIEW.DASHBOARD ? 'active' : ''}`}
                onClick={() => setActiveTab(QUERY_VIEW.DASHBOARD)}
              >
                Dashboard
              </li>
              <li
                className={`tabItem ${activeTab === QUERY_VIEW.EPHARMA ? 'active' : ''}`}
                onClick={() => setActiveTab(QUERY_VIEW.EPHARMA)}
              >
                DG ePharma
              </li>
              <li
                className={`tabItem ${activeTab === QUERY_VIEW.EMED ? 'active' : ''}`}
                onClick={() => setActiveTab(QUERY_VIEW.EMED)}
              >
                DG eMed
              </li>
            </ul>
          </div>

          {activeTab === QUERY_VIEW.DASHBOARD && (
            <div className="dashboardSection mt-4">
              {loading ? (
                <div className="loadingState">Loading dashboard...</div>
              ) : (
                (() => {
                  const dash = getDashboardData()
                  return (
                    <>
                      <div className="dashboardCards dFlex">
                        <div className="dashCard dashCardTotal">
                          <div className="dashCardValue">{dash.totalAll}</div>
                          <div className="dashCardLabel">Total Queries</div>
                        </div>
                        <div className="dashCard dashCardEpharma">
                          <div className="dashCardValue">{dash.totalEpharma}</div>
                          <div className="dashCardLabel">DG ePharma</div>
                        </div>
                        <div className="dashCard dashCardEmed">
                          <div className="dashCardValue">{dash.totalEmed}</div>
                          <div className="dashCardLabel">DG eMed</div>
                        </div>
                      </div>

                      <div className="dashboardCards dFlex mt-4">
                        <div className="dashCard dashCardStatus">
                          <div className="dashCardValue statusTextNew">{dash.statusCounts.NEW}</div>
                          <div className="dashCardLabel">New</div>
                        </div>
                        <div className="dashCard dashCardStatus">
                          <div className="dashCardValue statusTextInProgress">{dash.statusCounts.IN_PROGRESS}</div>
                          <div className="dashCardLabel">In Progress</div>
                        </div>
                        <div className="dashCard dashCardStatus">
                          <div className="dashCardValue statusTextResolved">{dash.statusCounts.RESOLVED}</div>
                          <div className="dashCardLabel">Resolved</div>
                        </div>
                        <div className="dashCard dashCardStatus">
                          <div className="dashCardValue statusTextClosed">{dash.statusCounts.CLOSED}</div>
                          <div className="dashCardLabel">Closed</div>
                        </div>
                      </div>

                      <div className="prioritySection mt-5">
                        <div className="prioritySectionTitle">Priority — Unresolved Queries (Past 6 Months)</div>
                        <div className="tableScrollWrap mt-3">
                          <table className="table productTable queryTable priorityTable">
                            <thead>
                              <tr>
                                <th width="6%">Priority</th>
                                <th width="8%">Query ID</th>
                                <th width="10%">Days Pending</th>
                                <th width="10%">Source</th>
                                <th width="16%">Name</th>
                                <th width="12%">Contact</th>
                                <th width="20%">Subject</th>
                                <th width="10%">Status</th>
                                <th width="8%">Assigned To</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dash.unresolvedQueries.length > 0 ? (
                                dash.unresolvedQueries.map((query, index) => (
                                  <tr
                                    key={query.query_id}
                                    className={`clickableRow ${query.daysPending >= 7 ? 'priorityHigh' : query.daysPending >= 3 ? 'priorityMedium' : ''}`}
                                    onClick={() => handleRowClick(query)}
                                  >
                                    <td>
                                      <span className={`priorityBadge ${query.daysPending >= 7 ? 'priorityBadgeHigh' : query.daysPending >= 3 ? 'priorityBadgeMedium' : 'priorityBadgeLow'}`}>
                                        {index + 1}
                                      </span>
                                    </td>
                                    <td>{query.query_id}</td>
                                    <td>
                                      <span className={`dayCount ${query.daysPending >= 7 ? 'dayCountHigh' : query.daysPending >= 3 ? 'dayCountMedium' : 'dayCountLow'}`}>
                                        {query.daysPending} {query.daysPending === 1 ? 'day' : 'days'}
                                      </span>
                                    </td>
                                    <td>
                                      {query.user_type === QUERY_TAB_TYPE.EPHARMA ? 'ePharma' : 'eMed'}
                                    </td>
                                    <td className="nameCell">
                                      <div className="dualDataCentered">
                                        <span>{query.name}</span>
                                        {query.user_type === QUERY_TAB_TYPE.EPHARMA && (
                                          <span className="subText">{query.user_name || '-'}</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="contactCell">
                                      <div className="dualDataCentered">
                                        <span>{query.mobile_no || '-'}</span>
                                        <span className="emailText">{query.email || '-'}</span>
                                      </div>
                                    </td>
                                    <td title={query.subject}>{query.subject}</td>
                                    <td>
                                      <span className={`statusBadge ${getStatusClass(query.status)}`}>
                                        {convertToTitleCase(query.status)}
                                      </span>
                                    </td>
                                    <td>{query.assigned_to_name || '-'}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="9" className="emptyState">
                                    No unresolved queries
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )
                })()
              )}
            </div>
          )}

          {activeTab !== QUERY_VIEW.DASHBOARD && (
            <div className="queryTableWrapper mt-4">
              {loading ? (
                <div className="loadingState">Loading queries...</div>
              ) : (
                <div className="tableScrollWrap">
                  <table className="table productTable queryTable priorityTable">
                    <thead>
                      <tr>
                        <th width="5%">Priority</th>
                        <th width="7%">Query ID</th>
                        <th width="9%">Days Pending</th>
                        <th width="15%">
                          {activeTab === QUERY_VIEW.EPHARMA
                            ? 'Pharmacy / User Name'
                            : 'Customer Name'}
                        </th>
                        <th width="10%">Contact Info</th>
                        <th width="12%">Date & Time</th>
                        <th width="16%">Subject</th>
                        <th width="10%">Status</th>
                        <th width="10%">Assigned To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredQueriesData?.length > 0 ? (
                        filteredQueriesData.map((query, index) => (
                          <tr
                            key={query.query_id}
                            className={`clickableRow ${query.daysPending >= 7 ? 'priorityHigh' : query.daysPending >= 3 ? 'priorityMedium' : ''}`}
                            onClick={() => handleRowClick(query)}
                          >
                            <td>
                              <span className={`priorityBadge ${query.daysPending >= 7 ? 'priorityBadgeHigh' : query.daysPending >= 3 ? 'priorityBadgeMedium' : 'priorityBadgeLow'}`}>
                                {index + 1}
                              </span>
                            </td>
                            <td>{query.query_id}</td>
                            <td>
                              <span className={`dayCount ${query.daysPending >= 7 ? 'dayCountHigh' : query.daysPending >= 3 ? 'dayCountMedium' : 'dayCountLow'}`}>
                                {query.daysPending} {query.daysPending === 1 ? 'day' : 'days'}
                              </span>
                            </td>
                            <td className="nameCell">
                              <div className="dualDataCentered">
                                <span>{query.name}</span>
                                {activeTab === QUERY_VIEW.EPHARMA && (
                                  <span className="subText">{query.user_name || '-'}</span>
                                )}
                              </div>
                            </td>
                            <td className="contactCell">
                              <div className="dualDataCentered">
                                <span>{query.mobile_no || '-'}</span>
                                <span className="emailText">{query.email || '-'}</span>
                              </div>
                            </td>
                            <td className="dateTimeCell">
                              <div className="dualDataCentered">
                                <span>{formatDate(query.date)}</span>
                                <span>{formatTime(query.date)}</span>
                              </div>
                            </td>
                            <td title={query.subject}>{query.subject}</td>
                            <td>
                              <span className={`statusBadge ${getStatusClass(query.status)}`}>
                                {convertToTitleCase(query.status)}
                              </span>
                            </td>
                            <td>{query.assigned_to_name || '-'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="emptyState">
                            No queries found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <SideDrawer
          handleDrawerToggle={handleDrawerToggle}
          open={open}
          drawerButton={canManage && nextAction !== null}
          buttonText={nextAction?.label}
          drawerHeading="Query Details"
          handleClick={() => {
            if (nextAction?.value === QUERY_STATUS.RESOLVED) {
              handleResolve()
            } else if (nextAction) {
              handleStatusUpdate(nextAction.value)
            }
          }}
        >
          <div className="wizardFieldset">
            <form className="mt-5">
              <div className="queryDetails">
                <div className="queryNumber">
                  {`Query No. ${queryDetails.query_id || ''}`}
                </div>
                <div className="queryDate">
                  {queryDetails.date &&
                    `${formatDate(queryDetails.date)} ${formatTime(queryDetails.date)}`}
                </div>
                <div className="queryStatusWrap mt-3">
                  <span className={`statusBadge ${getStatusClass(queryDetails.status)}`}>
                    {convertToTitleCase(queryDetails.status)}
                  </span>
                </div>
              </div>

              <div className="formGroup formFloating fgrow1">
                <input
                  type="text"
                  className="formControl"
                  placeholder="Name"
                  value={queryDetails?.name || ''}
                  disabled
                />
                <label>{queryDetails?.user_type === 'PHARMACY_STORE' ? 'Branch Name' : 'Name'}</label>
              </div>

              {queryDetails?.user_type === 'PHARMACY_STORE' && queryDetails?.user_name && (
                <div className="formGroup formFloating fgrow1">
                  <input
                    type="text"
                    className="formControl"
                    placeholder="User Name"
                    value={queryDetails?.user_name || ''}
                    disabled
                  />
                  <label>User Name</label>
                </div>
              )}

              <div className="formGroup formFloating fgrow1">
                <input
                  type="text"
                  className="formControl"
                  placeholder="Email"
                  value={queryDetails?.email || '-'}
                  disabled
                />
                <label>Email</label>
              </div>

              <div className="formInline dFlex flexNowrap">
                <div className="formGroup formFloating fgrow1 maxWidth">
                  <select className="formSelect" disabled>
                    <option value="2">+88</option>
                  </select>
                  <label>Country</label>
                </div>
                <div className="formGroup formFloating ml-4 fgrow1">
                  <input
                    type="text"
                    className="formControl"
                    placeholder="Phone Number"
                    value={queryDetails?.mobile_no || ''}
                    disabled
                  />
                  <label>Phone Number</label>
                </div>
              </div>

              {queryDetails?.address && (
                <div className="formGroup formFloating">
                  <input
                    type="text"
                    className="formControl"
                    placeholder="Address"
                    value={queryDetails?.address || ''}
                    disabled
                  />
                  <label>Address</label>
                </div>
              )}

              <div className="formGroup formFloating">
                <input
                  type="text"
                  className="formControl"
                  placeholder="User Type"
                  value={convertToTitleCase(queryDetails?.user_type) || ''}
                  disabled
                />
                <label>User Type</label>
              </div>

              <div className="formGroup formFloating">
                <input
                  type="text"
                  className="formControl"
                  placeholder="Subject"
                  value={queryDetails?.subject || ''}
                  disabled
                />
                <label>Subject</label>
              </div>

              <div className="formGroup formFloating">
                <textarea
                  className="formControl textarea"
                  placeholder="Query"
                  value={queryDetails?.query || ''}
                  disabled
                  rows="5"
                ></textarea>
                <label>Query</label>
              </div>

              {canManage && (
                <>
                  <div className="drawerSectionTitle">Assign To</div>
                  <Select
                    className="formSelect reactSelect mb-4"
                    value={
                      queryDetails?.assigned_to_id
                        ? {
                            value: queryDetails.assigned_to_id,
                            label: queryDetails.assigned_to_name,
                          }
                        : null
                    }
                    options={crmUsers?.map((user) => ({
                      value: user.id,
                      label: user.name,
                    }))}
                    onChange={(option) => handleAssign(option?.value)}
                    placeholder="Select CRM User"
                    styles={reactSelectStyles}
                    isClearable
                  />

                  <div className="drawerSectionTitle">Internal Notes</div>
                  <div className="formGroup formFloating">
                    <textarea
                      className="formControl textarea"
                      placeholder="Add a note..."
                      value={noteInput}
                      onChange={(e) => setNoteInput(e.target.value)}
                      rows="3"
                    ></textarea>
                    <label>Add a note...</label>
                  </div>
                  <button
                    type="button"
                    className="btn btnPrimaryOutlined mb-4"
                    onClick={handleSaveNotes}
                    disabled={!noteInput.trim()}
                  >
                    Save Notes
                  </button>

                  {notesHistory.length > 0 && (
                    <div className="notesHistory mb-4">
                      <div className="notesHistoryTitle">Notes History</div>
                      <div className="notesHistoryList">
                        {[...notesHistory].reverse().map((note, idx) => (
                          <div key={idx} className="noteItem">
                            <div className="noteItemMeta">
                              <span className="noteItemUser">{note.created_by}</span>
                              <span className="noteItemDate">{note.created_at}</span>
                            </div>
                            <div className="noteItemText">{note.text}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {queryDetails?.attachment_name && (
                    <>
                      <div className="drawerSectionTitle">Attachments</div>
                      <div className="attachmentItem mb-4">
                        <a
                          href={`${Config.getBackendOrigin()}${queryDetails?.attachment_url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {queryDetails.attachment_name}
                        </a>
                      </div>
                    </>
                  )}
                </>
              )}
            </form>
          </div>
        </SideDrawer>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    loginData: state.loginDataReducer?.loginData,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(Query)
